"use client";

import { usePendingWakeCancel, WAKE_WORD_CANCEL_PLACEHOLDER } from '@/lib/wake-word/use-pending-cancel';
import { useIsMobile } from "@/lib/viewport/client";
import { collapseIntoOrb } from "@/lib/ui/glass-exit";
import { useNetworkStatus } from "@/lib/offline/use-network-status";

import { Fragment, useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { usePathname, useRouter, useSelectedLayoutSegment } from "next/navigation";
import {
  Calendar,
  Camera,
  Clock,
  ChevronDown,
  ChevronUp,
  FileText,
  Gauge,
  House,
  Image as ImageIcon,
  Film,
  Inbox,
  LoaderCircle,
  MapPin,
  Menu,
  Mic,
  Square,
  Plus,
  Maximize2,
  RefreshCw,
  Send,
  User,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Icon } from "@/components/ui/Icon";
import { MENU_ROW_ICONS, SKILLS_BY_CATEGORY } from "./menu-pages";
import { openDirections } from "@/lib/native/maps";
import type { NavAction } from "@/lib/chat/nav-action";
import { openOfflineOverlay, resolveNavAction } from "@/lib/offline/overlay-events";
import { useDeadNavProbe } from "@/lib/offline/dead-nav-probe";
import { hidesAppChrome, isOfflineShellSegment } from "./chrome-hidden-paths";
import { SUBBIE_ROLE_SLUG } from "@/lib/workspace/subbie-switcher";
import { useOfflineSurfaceOpen } from "@/lib/offline/offline-surface";
import { useIntroSurfaceOpen } from "@/lib/onboarding/intro-surface";
import { useChromeCovered } from "@/lib/ui/chrome-cover";
import { useBarYielded } from "@/lib/ui/bar-yield";
import { useNativeBackSwipe } from "@/lib/native/back-swipe";
import { hasNativePlugin } from "@/lib/native/is-native";
import { KEYBOARD_EVENT } from "@/lib/native/keyboard";
import { unlockIOSAudio } from "@/lib/voice/audio-unlock";
import {
  BAR_ATTACH_EVENT,
  BAR_OPEN_EVENT,
  BAR_RESET_EVENT,
  BAR_SEARCH_EVENT,
  BAR_VOICE_START_EVENT,
  type BarAttachKind,
  getChatSnapshot,
  getServerChatSnapshot,
  requestBarChoicePick,
  requestBarDelete,
  requestBarSend,
  requestBarStagedDecision,
  requestBarLineStep,
  subscribeChat,
} from "@/lib/chat/bar-bridge";
import ChoiceChips from "@/components/ui/ChoiceChips";
import { BubbleActions, BubbleRows } from "@/components/chat/BubbleBlock";
import { UiBlocks, blocksReplaceJobRows } from "@/components/chat/UiBlockView";
import { documentCardActionIds } from "@/lib/chat/blocks/document-card";
import { jobListRows, navActionRows } from "@/lib/chat/message-rows";
import { latestStagedIndex as latestStagedIndexFor } from "@/lib/chat/staged-position";
import { waitingCardId, transcriptRestingTop } from "@/lib/chat/transcript-pin";
import { newStagedIds } from "@/lib/chat/staged-reveal";
import { useConnectingLabel } from "@/hooks/useConnectingLabel";
import { useUiGlass, useUiGlassNative } from "@/lib/ui-variant/use-ui-glass";
import NativeGlassBar from "./NativeGlassBar";
import { GlassBar, type GlassBarMic } from "@/lib/native/glass-bar";

/**
 * The Mucka Bar — the mobile bottom nav, and the place you talk to Mucka.
 *
 * The nav this replaces made you LEAVE the page to ask a question: the bolt
 * opened a full-screen chat over whatever you were looking at. Here the
 * composer is docked into the bar, so the answer arrives with the job list
 * still on screen.
 *
 * The knock-on is that the bolt moved into the composer as an ordinary mic,
 * which is what let the hex cutout and the pedestal go — nothing overhangs the
 * bar any more, so it can be a plain chamfered sheet.
 *
 * Layers, top to bottom, each optional:
 *   caret       shown once the composer is out; closes it, and while a
 *               conversation is up doubles as the resize grip
 *   transcript  text bubbles from the live conversation
 *   attach      the 2x2 the + reveals
 *   composer    + · input · mic
 *   tabs        Home · Diary · Ask Mucka · Clients · Menu
 *               (Clients’ column becomes Actions on a seat without
 *               `view_clients` — the engineer’s bar)
 *
 * The conversation itself belongs to MuckaPanel, which owns streaming,
 * attachments and action cards. This reads a published view of it and asks
 * MuckaPanel to send — see `src/lib/chat/bar-bridge.ts` for why.
 */

type TabKey = "home" | "diary" | "clients" | "actions" | "menu" | "calculators" | "timesheets";

const TAB_PATHS: Partial<Record<TabKey, string>> = {
  // 🚨 '/overview', not '/'. On mobile `/` is not a page — RootLandingRedirect
  // bounces it to /overview (and desktop to /schedule). Pushing '/' therefore
  // cost a client redirect to arrive exactly where a tap on Home should have
  // gone directly, and left the user on a URL this map had never heard of.
  home: "/overview",
  diary: "/schedule",
  clients: "/clients",
  actions: "/actions",
  calculators: "/gas-calculators",
  timesheets: "/timesheets",
};

/**
 * Which tab is lit for the page you are standing on.
 *
 * 🚨 DERIVED from TAB_PATHS, never hand-kept. It used to be a second literal
 * naming four of the six paths, which is CLAUDE.md #28 in a component: two
 * lists of the same thing, and the second is where entries go to die.
 *
 * Three tabs never lit as a result. **Home** was the one that cost something:
 * a mobile owner's home page is /overview, this map only knew '/', so the tab
 * showed unselected on the very page it points at — and tapping it pushed '/',
 * which redirected straight back to /overview with nothing visibly moving and
 * the tab still dark. One window-fitting owner rage-clicked it on 8 September
 * (MUCKA-WEB-7T): the bar told him he was not home, so he kept pressing Home.
 * Calculators and Timesheets had the same dead highlight with no redirect
 * behind it, unreported.
 *
 * Nothing could catch it — both literals were internally consistent, `tsc` sees
 * two valid records, and the fault was a URL absent from one of them.
 */
const PATH_TO_TAB: Record<string, TabKey> = {
  ...Object.fromEntries(
    Object.entries(TAB_PATHS).map(([tab, path]) => [path, tab as TabKey]),
  ),
  // The bare root still resolves, for the frame between landing on it and the
  // redirect committing. Listed after the spread so it cannot shadow a real one.
  "/": "home",
};

type Tab = { key: TabKey; icon: LucideIcon; label: string };

const LEFT_TABS: Tab[] = [
  { key: "home", icon: House, label: "Home" },
  { key: "diary", icon: Calendar, label: "Diary" },
];
const RIGHT_TABS: Tab[] = [
  { key: "clients", icon: User, label: "Contacts" },
  { key: "menu", icon: Menu, label: "Menu" },
];

/** Takes the Contacts column on a seat that has no Contacts — see the
 *  RIGHT_TABS branch in the render for who that is and why. Same Inbox icon
 *  the desktop sidebar uses for the same destination. */
const ACTIONS_TAB: Tab = { key: "actions", icon: Inbox, label: "Actions" };

/**
 * The two tabs a subcontractor can earn back.
 *
 * A subbie's bar is Home · Diary · Menu, which is right but thin — and the two
 * things they DO have, when the workspace has bought them, are the calculators
 * and their own hours. Both are otherwise buried in the Menu drawer, and a
 * subbie logging a day is a several-times-a-week job, not a menu-dive.
 *
 * Each needs BOTH the skill installed on the account AND the permission on the
 * seat, because the two answer different questions: "has this firm got it?" and
 * "is this person allowed it?". `view_own_hours` in particular is off until the
 * owner turns on Timesheets' `worker_self_capture`, so the tab appears exactly
 * when self-logging starts working and not a moment before.
 *
 * Both permissions sit INSIDE the subbie ceiling (src/lib/auth/role-ceilings.ts),
 * which is what makes this safe to key off a permission at all.
 */
const SUBBIE_WORK_TABS: (Tab & { slug: string; permission: string })[] = [
  { key: "calculators", icon: Gauge, label: "Calcs", slug: "gas-calculators", permission: "use_gas_calculators" },
  { key: "timesheets", icon: Clock, label: "Hours", slug: "timesheets", permission: "view_own_hours" },
];

/**
 * Liquid Glass (variant B): the icon for the circle the tabs fold into, for
 * any page — the tab's own icon, or the one the page already has in the Menu.
 * Longest path first, so /settings/referrals wins over /settings and a job's
 * own page (/jobs/123) reads as Live Jobs. Nothing here decides navigation.
 */
const PAGE_ICONS: [string, LucideIcon][] = [
  ...[...LEFT_TABS, ...RIGHT_TABS, ACTIONS_TAB, ...SUBBIE_WORK_TABS].flatMap(
    (t): [string, LucideIcon][] => (TAB_PATHS[t.key] ? [[TAB_PATHS[t.key] as string, t.icon]] : []),
  ),
  ...(Object.entries(MENU_ROW_ICONS) as [string, LucideIcon][]),
  ...Object.values(SKILLS_BY_CATEGORY).flat().map((s): [string, LucideIcon] => [s.href, s.icon]),
].sort((a, b) => b[0].length - a[0].length);

function pageIcon(pathname: string): LucideIcon | undefined {
  return PAGE_ICONS.find(([href]) => pathname === href || pathname.startsWith(`${href}/`))?.[1];
}

const ATTACH_ITEMS: { key: BarAttachKind; label: string; icon: LucideIcon }[] = [
  { key: "photos", label: "Photos", icon: ImageIcon },
  { key: "camera", label: "Camera", icon: Camera },
  { key: "document", label: "Document", icon: FileText },
  { key: "location", label: "Location", icon: MapPin },
];

/**
 * Keyed by `AttachmentKind` rather than a hand-written union, so a kind added
 * to the chat bar has to be given an icon here before this compiles. It was a
 * hand-written union until video arrived, and tsc is the only thing that
 * noticed — the runtime would have handed `<Icon icon={undefined}>` to a chip
 * that still rendered, just with nothing in it.
 */
const ATTACH_ICONS: Record<"image" | "video" | "document" | "location", LucideIcon> = {
  image: ImageIcon,
  video: Film,
  document: FileText,
  location: MapPin,
};

/** Docked height of the transcript, and the floor a drag can shrink it to. */
const TRANSCRIPT_H_DEFAULT = 190;
const TRANSCRIPT_H_MIN = 96;
/** A drag never takes the sheet past this share of the viewport: sitting
 *  alongside the page is the whole point of docking it. */
const SHEET_MAX_SHARE = 0.8;

/** The Actions count AuthedShell publishes for the Menu tab's number. */
function subscribeToNavBadges(onChange: () => void) {
  window.addEventListener("mucka-nav-badges", onChange);
  return () => window.removeEventListener("mucka-nav-badges", onChange);
}

function getNavActions(): number {
  return (window as unknown as { __muckaNavActions?: number }).__muckaNavActions ?? 0;
}

/** External-store adapter over the permissions AuthedShell publishes. */
function subscribeToNavPerms(onChange: () => void) {
  window.addEventListener("mucka-nav-perms", onChange);
  return () => window.removeEventListener("mucka-nav-perms", onChange);
}

function getCanViewClients() {
  const perms = (window as unknown as { __muckaNavPerms?: Record<string, boolean> }).__muckaNavPerms;
  // No value yet (pre-hydration, or a page outside AuthedShell) → allow, and
  // let the route itself do the enforcing.
  return perms ? perms['view_clients'] === true : true;
}

/**
 * Whether this seat can open the action inbox.
 *
 * Only ever consulted for a seat WITHOUT `view_clients` — see the RIGHT_TABS
 * branch below. Unset (pre-hydration, or a page outside AuthedShell) → false,
 * which is the opposite default to `getCanViewClients` and deliberate: an
 * unknown permission there costs a tab that 404s, whereas here it would cost a
 * tab appearing and then vanishing under the owner's thumb on hydration. The
 * spacer is the honest thing to show while we don't know.
 */
function getCanViewActions() {
  const perms = (window as unknown as { __muckaNavPerms?: Record<string, boolean> }).__muckaNavPerms;
  return perms ? perms['view_actions'] === true : false;
}

function getNavPerms(): Record<string, boolean> {
  return (window as unknown as { __muckaNavPerms?: Record<string, boolean> }).__muckaNavPerms ?? {};
}

/** Skill slugs installed on the active account, as a stable-identity string.
 *  Joined rather than returned as an array because useSyncExternalStore
 *  compares snapshots with Object.is, and a fresh array every render is an
 *  infinite loop. */
function getEnabledSkillsKey(): string {
  const slugs = (window as unknown as { __muckaNavSkills?: string[] }).__muckaNavSkills;
  return slugs ? slugs.join(",") : "";
}

function getCanUseChat() {
  // The ROLE, not `use_chat` — and the two now AGREE, which they did not before.
  //
  // 🚨 The comment that stood here was wrong, and worth recording because it
  // read as reassurance. It said engineers "talk to Mucka from their phones all
  // day today" on the grounds that this bar's send path sits outside
  // MuckaPanel's desktop chat gate. The send path does sit outside it — and
  // lands on POST /api/engine/chat, which checks `use_chat` and 403s. No
  // engineer role in any environment held that key, so the bar took their
  // typing and threw it away. Engineers now hold `use_chat`
  // (src/lib/auth/default-roles.ts), so the bar and the endpoint finally say
  // the same thing.
  //
  // Still keyed on the role because that is what separates an engineer from a
  // subcontractor for the COMPOSER specifically: `use_chat` is outside
  // SUBBIE_PERMISSION_CEILING, so a subbie reads false either way, and keying
  // on the role keeps this honest for a custom role that has chat turned off
  // deliberately.
  //
  // Unset (pre-hydration, or a page outside AuthedShell) → allow, matching
  // getCanViewClients: the bar is cosmetic and the routes behind it enforce
  // themselves.
  const role = (window as unknown as { __muckaNavRole?: string }).__muckaNavRole;
  return role ? role !== SUBBIE_ROLE_SLUG : true;
}

function formatBubbleTime(time?: number): string {
  if (!time) return "";
  return new Date(time).toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" });
}

export default function MobileNav() {
  const isMobile = useIsMobile();
  const online = useNetworkStatus();
  const [listening, setListening] = useState(false);
  const [voiceConnecting, setVoiceConnecting] = useState(false);
  // A native call lost signal and is holding on for it (TKT-1462).
  const [voiceReconnecting, setVoiceReconnecting] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [composerOpenState, setComposerOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  // Liquid Glass (variant B): the open conversation is full screen, and this
  // folds it to a slim bar above the composer so the app can be used while
  // talking. Only the glass minimise button sets it; variant A never does.
  const [chatMinimised, setChatMinimised] = useState(false);
  const [draft, setDraft] = useState("");
  const [transcriptH, setTranscriptH] = useState(TRANSCRIPT_H_DEFAULT);
  const [dragging, setDragging] = useState(false);
  const [keyboardH, setKeyboardH] = useState(0);
  // Whether the composer input holds focus — i.e. the user is typing.
  //
  // The tabs hide on THIS rather than on the keyboard being detected, because
  // detecting the keyboard is not reliable in the Android WebView. There is no
  // `windowSoftInputMode` in AndroidManifest.xml, so the activity adjust-resizes:
  // the whole window shrinks when the keyboard opens, which shrinks
  // `window.innerHeight` — and that is the very term the visualViewport
  // measurement subtracts, so the computed inset collapses to ~0 and
  // `keyboard-open` never goes on. Chrome on Android shrinks only the VISUAL
  // viewport, which is why the same build hides the tabs correctly in the
  // browser and not in the app.
  //
  // Focus sidesteps the whole question. "Are the tabs in the way?" is really
  // "is the user typing?", which the input already knows first-hand on every
  // platform, with no geometry involved. The keyboard height is still used for
  // the iOS lift, where it IS reliable.
  const [typing, setTyping] = useState(false);
  /** A search field elsewhere in the tree holds focus — see BAR_SEARCH_EVENT below. */
  const [searchFocused, setSearchFocused] = useState(false);
  // The native glass bar trial (PR 4): the tabs and composer row are drawn by
  // MuckaGlassBarPlugin over the WebView, from this component's state. The web
  // bar below still renders (its rows hidden), so nothing here forks.
  const nativeBar = useUiGlassNative();

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dragRef = useRef<{ y: number; h: number; max: number; moved: boolean } | null>(null);

  // Which tabs this role is allowed. AuthedShell holds the resolved permissions
  // but this component is mounted in app/layout.tsx, OUTSIDE it, so it can't
  // receive them as props — AuthedShell stashes them on `window` and fires
  // "mucka-nav-perms", and we treat that as an external store.
  //
  // useSyncExternalStore rather than an effect on purpose. React runs effects in
  // tree order and this is a later sibling of {children}, so AuthedShell's
  // effect has always already fired by the time we would subscribe: an
  // event-only approach misses the value on every first paint and the tab never
  // hides. Reading the store during render has no such ordering problem.
  //
  // Defaults to allowed — the bar is cosmetic, and every route behind it
  // enforces its own permission, so the worst case is a tab that 404s.
  const canViewClients = useSyncExternalStore(
    subscribeToNavPerms,
    getCanViewClients,
    () => true,
  );

  // Liquid Glass: the number on the Menu tab is the Actions count (Tom,
  // 2026-10-14) — the Menu sheet numbers Actions and the Inbox row by row.
  // On a seat whose bar carries the Actions tab itself, the number goes there.
  const glass = useUiGlass();
  const actionsWaiting = useSyncExternalStore(subscribeToNavBadges, getNavActions, () => 0);

  // Fills the column Contacts leaves behind — see the RIGHT_TABS branch below.
  const canViewActions = useSyncExternalStore(
    subscribeToNavPerms,
    getCanViewActions,
    () => false,
  );

  // Whether this seat can talk to Mucka at all. False for the subcontractor
  // role and no other, permanently — `use_chat` sits outside the subbie
  // permission ceiling, so no owner can switch it back on.
  //
  // When false the bar loses Ask Mucka ENTIRELY — not a disabled tab, not a
  // decorative mark. A subcontractor gets Home · Diary · Menu and nothing else,
  // so the bar is three things they can all actually press. Keeping the M as a
  // logo was tried first and read as a broken button (Tom, 2026-08-14).
  const canUseChat = useSyncExternalStore(
    subscribeToNavPerms,
    getCanUseChat,
    () => true,
  );

  // Which of the two extra tabs this subcontractor has earned. Empty for every
  // other role: this is deliberately not a general "put skills on the bar"
  // feature — owners and engineers have Ask Mucka in column 3 and reach these
  // from the Menu, and widening the bar for them is a different decision.
  const enabledSkillsKey = useSyncExternalStore(
    subscribeToNavPerms,
    getEnabledSkillsKey,
    () => "",
  );
  const subbieWorkTabs = canUseChat
    ? []
    : SUBBIE_WORK_TABS.filter(t => {
        const perms = typeof window === "undefined" ? {} : getNavPerms();
        return enabledSkillsKey.split(",").includes(t.slug) && perms[t.permission] === true;
      });

  // The live conversation, published by MuckaPanel.
  const chat = useSyncExternalStore(subscribeChat, getChatSnapshot, getServerChatSnapshot);

  const pathname = usePathname();
  // Which route the page's TREE belongs to — read from the server payload, not
  // the address bar. The service worker answers a failed navigation with the
  // offline page's HTML while the URL keeps what was asked for, so on
  // `/clients` the server HTML has no bar (it was rendered for `/offline`) and
  // `pathname` says draw one. That disagreement during hydration is
  // MUCKA-WEB-AY: "Hydration failed" and the whole page rebuilt, on every
  // native-app cold start without signal. The segment is the same on server
  // and client, so this check is the one that can agree with the HTML.
  const offlineShell = isOfflineShellSegment(useSelectedLayoutSegment());
  const offlineSurfaceOpen = useOfflineSurfaceOpen();
  const introSurfaceOpen = useIntroSurfaceOpen();
  // The Face ID lock and the "Lock with Face ID" offer cover the whole app.
  const chromeCovered = useChromeCovered();
  // A page with its own bottom row (a certificate's dock) takes the bar's
  // place. Never while Mucka is on a call or the chat is open: the bar is the
  // only thing on screen saying a call is live, and the only way to end it.
  const barYielded = useBarYielded() && !listening && !composerOpenState;
  // The iPhone's swipe from the left edge goes Back — never under a sheet.
  useNativeBackSwipe(chromeCovered);
  // Every reason below that the bar is not drawn, in one place, so the native
  // bar can be told about all of them.
  const barDrawn =
    isMobile && !hidesAppChrome(pathname) && !offlineShell && !offlineSurfaceOpen && !introSurfaceOpen && !chromeCovered && !barYielded;
  const drawsNativeBar = barDrawn && nativeBar;
  // 🚨 The native glass bar is a view ABOVE the WebView, and it outlives the
  // page. A full document load — signing out POSTs a form, an update reload,
  // a workspace switch — throws the old page's JS away without unmounting
  // NativeGlassBar, so nothing told the bar to go and the login page came up
  // under Home / Diary / Ask Mucka (Tom, 2026-10-11). So any page NOT drawing
  // it says so, on every route: including a fresh document, where the flag
  // that would mount NativeGlassBar is not even set yet. No-op where the
  // plugin is missing, and on a build where no bar was ever made.
  useEffect(() => {
    if (drawsNativeBar || !hasNativePlugin("MuckaGlassBar")) return;
    GlassBar.setMode({ mode: "hidden", animated: false }).catch(() => {});
  }, [drawsNativeBar, pathname]);
  const router = useRouter();
  const armDeadNav = useDeadNavProbe();

  const connectingLabel = useConnectingLabel(voiceConnecting);
  // The docked bar is on every mobile screen, so it is the one place an owner
  // who has noticed the recording indicator is certain to be looking.
  // Non-null only while a WAKE-WORD session is between hearing and connecting.
  // Deliberately not offered for the mic button: pressing that is a decision
  // you made, and a cancel on it is noise on a control used constantly.
  const cancelWake = usePendingWakeCancel();

  // `canUseChat &&` is the belt to handleAskTap's braces: the composer also
  // opens on BAR_EXPAND_EVENT and on a voice start, neither of which comes from
  // the tab, so gating only the tap would leave a route in.
  const composerOpen = (composerOpenState || !!cancelWake) && canUseChat;
  const showTranscript = composerOpen && (chat.messages.length > 0 || chat.thinking);
  // The full-screen conversation is up, with its own X in the header. Glass
  // then drops the fold circle beside the composer: two buttons that both put
  // Mucka away, and the text box gets the room back (Tom, 2026-10-10).
  // 🚨 NOT `showTranscript`: glass draws the full screen and its header the
  // moment the composer opens, messages or not. Keyed on the transcript, a
  // fresh chat still showed both buttons, on the web bar and the native one.
  // Minimised, the circle stays: it is the way back to the tabs there.
  const convoFull = composerOpen && !chatMinimised;
  /**
   * The header stays on screen while a call is live, and goes orange.
   *
   * Voice survives navigation and survives the sheet being put away, which is
   * the whole point of it — but until this there was NOTHING on screen saying
   * so once the composer was closed. The mic lives inside the composer, so
   * minimising took away the only evidence a call was running and the only way
   * to end it, and the bar looked exactly as it does when nothing is happening.
   *
   * Orange whether open or minimised, deliberately: the question it answers is
   * "am I still connected", and that question does not change when the sheet
   * does. `listening` and not `voiceConnecting` — a connect that fails would
   * otherwise leave the bar orange with nothing on the other end, and the
   * composer already says "Connecting…" while it negotiates.
   */
  const voiceLive = listening;
  /** Live, with the sheet put away — the banner IS the whole surface. */
  const barMinimised = voiceLive && !composerOpen;
  /** Liquid Glass (variant B) only: the last thing said, for the minimised bars. */
  const lastLine = [...chat.messages].reverse().find((m) => m.text)?.text;
  /** Liquid Glass (variant B) only: the line under "Mucka" in the full-screen header. */
  const chatStatus =
    connectingLabel ??
    (voiceReconnecting ? "Reconnecting" : listening ? "Listening" : chat.thinking ? "Thinking…" : "Ready when you are");
  // Only the newest set of chips is tappable — see MuckaPanel for why.
  const lastChoiceIndex = chat.messages.reduce(
    (last, msg, idx) => (msg.choices ? idx : last),
    -1,
  );
  // A rewritten approval card draws only where it was last seen. Same rule as
  // the panel, from the same module, so the two surfaces cannot disagree about
  // which copy of a superseded draft is the live one.
  const stagedAt = latestStagedIndexFor(chat.messages);

  // 🚨 A card that arrives while the sheet is put away comes up with it. During
  // a live call the sheet folds to a banner on any tab tap, and the card only
  // draws inside the sheet — so "tap Send email below" pointed at nothing
  // (TKT-1346). Reasoning: `staged-reveal.ts`.
  //
  // 🚨 A MINIMISED glass conversation counts as put away. The sheet keeps
  // `--open` there and only the CSS hides the transcript, so "is the sheet
  // open?" alone left the card drawn where nobody could see it (MUCKA-WEB-CZ).
  const stagedSeenRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const { fresh, seen } = newStagedIds(stagedSeenRef.current, chat.messages);
    stagedSeenRef.current = seen;
    if (!fresh.length || !voiceLive || !canUseChat) return;
    if (!composerOpenState) setComposerOpen(true);
    if (chatMinimised) setChatMinimised(false);
  }, [chat.messages, voiceLive, composerOpenState, chatMinimised, canUseChat]);

  // VoiceProvider lives inside AuthedShell (this component is mounted in
  // app/layout.tsx, outside it), so we can't useVoice() directly. Instead
  // VoiceProvider dispatches a window event we subscribe to here.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ active: boolean; state?: string; reconnecting?: boolean }>).detail;
      setListening(Boolean(detail?.active));
      setVoiceReconnecting(Boolean(detail?.active && detail?.reconnecting));
      // A session takes a couple of seconds to negotiate. Without this the mic
      // sits there looking untapped and people tap it again.
      setVoiceConnecting(detail?.state === "connecting");
    };
    window.addEventListener("voice-state-change", handler);
    return () => window.removeEventListener("voice-state-change", handler);
  }, []);

  // Multiple sources can each "vote" for dark mode (chat, menu, actions
  // stack, heads-up stack, the map page itself). We refcount the votes
  // so that closing one overlay while another is still active doesn't
  // accidentally flip the bar back to light. Each dispatcher is required
  // to fire `open: true` on mount and `open: false` on unmount so the
  // count balances.
  useEffect(() => {
    let count = 0;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ open: boolean }>).detail;
      count = Math.max(0, count + (detail?.open ? 1 : -1));
      setDarkMode(count > 0);
    };
    window.addEventListener("mucka-actions-stack-state", handler);
    return () => window.removeEventListener("mucka-actions-stack-state", handler);
  }, []);

  // The Menu tab's highlight follows the drawer itself, published by
  // MobileMenu. A local flag set on tap would stay lit when the drawer closes
  // some other way — a row that navigates, a route change, the backdrop.
  useEffect(() => {
    const handler = (e: Event) => {
      setMenuOpen(Boolean((e as CustomEvent<{ open: boolean }>).detail?.open));
    };
    window.addEventListener("mucka-menu-state", handler);
    return () => window.removeEventListener("mucka-menu-state", handler);
  }, []);

  // Home's search field holds focus somewhere else in the tree entirely, so the
  // bar has to be told. Without this the tabs stay up and — on Android, where
  // dvh already reports the shrunk viewport — the whole bar rides the keyboard
  // and sits across the results the user is trying to read.
  //
  // Same answer as the composer's own `typing`: hide the tabs off an input's
  // FOCUS, never off keyboard detection, which the Android WebView cannot do
  // reliably. The composer knows its input first-hand; this is that signal
  // arriving from another tree.
  useEffect(() => {
    const handler = (e: Event) => {
      setSearchFocused(Boolean((e as CustomEvent<{ open: boolean }>).detail?.open));
    };
    window.addEventListener(BAR_SEARCH_EVENT, handler);
    return () => window.removeEventListener(BAR_SEARCH_EVENT, handler);
  }, []);

  // Pin the transcript to its newest message. It is a capped scroller, so it
  // would otherwise open at the top with the latest reply hidden under the
  // composer — the opposite of what a chat should do.
  //
  // 🚨 Except over a card waiting for a tap during a call: the pin stops with
  // its Send button at the foot of the box, or the call talking on underneath
  // pushes it out of the top (MUCKA-WEB-CZ). Reasoning: `transcript-pin.ts`.
  const heldCard = voiceLive
    ? waitingCardId(chat.messages, stagedAt, chat.stagedStates, (i, id) => documentCardActionIds(chat.messages[i].blocks).has(id))
    : null;
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = transcriptRestingTop(el, heldCard);
  }, [chat.messages, chat.thinking, transcriptH, attachOpen, showTranscript, keyboardH, heldCard]);

  // The keyboard shortens the sheet (see the keyboard block in globals.css), and
  // a scroller keeps its scrollTop when it shrinks — so the newest message ends
  // up below the fold at the exact moment the user has started typing. Tracking
  // the height re-runs the pin above; the value is deliberately unused in the
  // markup, the CSS does the moving.
  useEffect(() => {
    const handler = (e: Event) => {
      setKeyboardH((e as CustomEvent<{ height: number }>).detail?.height ?? 0);
    };
    window.addEventListener(KEYBOARD_EVENT, handler);
    return () => window.removeEventListener(KEYBOARD_EVENT, handler);
  }, []);

  const closeComposer = useCallback(() => {
    // Glass: the full-screen card shrinks back into the orb rather than
    // vanishing. A copy plays it; the state below changes straight away.
    if (sheetRef.current?.classList.contains("mucka-bar-sheet--convo")) collapseIntoOrb(sheetRef.current);
    setComposerOpen(false);
    setAttachOpen(false);
    setChatMinimised(false);
  }, []);

  /**
   * Bring the sheet back from the minimised banner.
   *
   * Neither this nor `handleAskTap` focuses the input, and the reason was first
   * written down here: raising the keyboard on somebody who is mid-sentence on
   * a voice call takes half the screen to offer them a way of typing they did
   * not ask for. They tapped the banner to see the conversation, not to write
   * in it — and the same is true of the tab. This one stays separate because it
   * does not toggle: a banner tap always means "show me", never "put it away".
   */
  const reopenFromBanner = useCallback(() => {
    if (menuOpen) window.dispatchEvent(new CustomEvent("mobile-modals-close"));
    setComposerOpen(true);
    // Glass minimises with the sheet still open, so "show me" has to unfold it
    // too, or a card asked for here stays hidden (MUCKA-WEB-CZ).
    setChatMinimised(false);
  }, [menuOpen]);

  /**
   * Liquid Glass (variant B): the X on the minimised bars and the full-screen
   * header. Ends the conversation outright — hangs up a call that is live or
   * still connecting, and puts the chat away. The transcript stays; start-again
   * is what clears it.
   */
  const endConversation = useCallback(() => {
    if (listening || voiceConnecting) window.dispatchEvent(new CustomEvent("mobile-voice-end"));
    closeComposer();
  }, [listening, voiceConnecting, closeComposer]);

  // Somebody outside the bar asked for the conversation to be visible — today
  // that is "Set up with Mucka", which starts a call from the Get Started
  // surface. Without this the call lands minimised: an orange banner, no
  // transcript, and nothing on screen of what Mucka is saying. The new-look
  // sheet's "Tell the team" sends a draft with it (`requestBarOpen`).
  useEffect(() => {
    const handler = (e: Event) => {
      reopenFromBanner();
      const draft = (e as CustomEvent<{ draft?: string } | undefined>).detail?.draft;
      if (typeof draft === "string") setDraft(draft);
    };
    window.addEventListener(BAR_OPEN_EVENT, handler);
    return () => window.removeEventListener(BAR_OPEN_EVENT, handler);
  }, [reopenFromBanner]);

  // Drag the caret to resize. Pointer events so one path covers mouse and
  // touch; setPointerCapture keeps the drag alive once the finger leaves the
  // grip, which on a strip this shallow it immediately does.
  const onGripDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!showTranscript) return;
    // Everything in the sheet that isn't the transcript — caret, attach grid if
    // it is out, composer, tabs. Measured now rather than assumed, because the
    // + can add a row at any point.
    const sheetH = sheetRef.current?.getBoundingClientRect().height ?? 0;
    const scrollH = scrollRef.current?.getBoundingClientRect().height ?? 0;
    const chrome = sheetH - scrollH;
    const max = Math.max(TRANSCRIPT_H_MIN, window.innerHeight * SHEET_MAX_SHARE - chrome);
    dragRef.current = { y: e.clientY, h: transcriptH, max, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
  };
  const onGripMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    // Dragging UP grows the transcript, so the delta is inverted.
    if (Math.abs(drag.y - e.clientY) > 3) drag.moved = true;
    setTranscriptH(Math.min(drag.max, Math.max(TRANSCRIPT_H_MIN, drag.h + (drag.y - e.clientY))));
  };
  const onGripUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    // A drag is not a tap. Only a still pointer closes the sheet, otherwise
    // every resize would dismiss the thing you were resizing.
    if (!drag || drag.moved) return;
    closeComposer();
  };

  if (!isMobile) return null;
  if (hidesAppChrome(pathname)) return null;
  if (offlineShell) return null;
  // Every tab here is a live-network navigation, so with no signal this is a
  // row of controls that do nothing. See offline-surface.ts for why the
  // pathname alone can't tell us.
  if (offlineSurfaceOpen) return null;
  // An intro sheet is up: the two-week offer, or the compulsory WhatsApp
  // pairing. Its tabs go nowhere a brand-new owner is allowed yet, and under
  // the undismissible one they are a row of controls that do nothing — the
  // same complaint that made the offline surface hide this bar.
  if (introSurfaceOpen) return null;
  if (chromeCovered) return null;
  if (barYielded) return null;

  const activeTab: TabKey | null = menuOpen ? "menu" : (PATH_TO_TAB[pathname] ?? null);
  // The folded tabs (variant B) show where you are, on every page.
  const foldIcon: LucideIcon = menuOpen ? Menu : (pageIcon(pathname) ?? House);

  // Connecting takes precedence: the spinning mic sits under the thumb that
  // just tapped it, so the placeholder is what actually gets seen.
  // 🚨 "Listening…" here means a LIVE CALL is listening to you. A dropped line
  // says nothing here: the pill over the blurred conversation already says
  // "Reconnecting", and the field is blurred with it (Tom, 8 Oct 2026). The
  // armed wake word says nothing either, by Tom's call the same day: the field
  // reads "Ask Mucka…" and the phone's own microphone dot is the sign.
  // 🚨 The cancel outranks the connecting label, and that ordering is the
  // feature. "Connecting…" is what the mic button says and it is fine there,
  // because you pressed it. For a session you did not ask for, the row has to
  // say what STARTED it and offer the way out — for the couple of seconds
  // where there is still something to get out of.
  const composerPlaceholder = cancelWake
    ? WAKE_WORD_CANCEL_PLACEHOLDER
    : connectingLabel ?? (voiceReconnecting ? "" : listening ? "Listening…" : "Ask Mucka…");

  // The right-hand columns, decided once for the web bar and the native one.
  const rightColumn: (Tab | null)[] = RIGHT_TABS.flatMap((tab): (Tab | null)[] => {
    if (tab.key !== "clients") return [tab];
    if (canViewClients) return [tab];
    // The engineer's column. Contacts is off their seat, so the fourth slot
    // used to be an empty square — a visible hole between Ask Mucka and Menu
    // on the one bar that could least afford it, since an engineer's bar is
    // the whole app to them.
    //
    // Actions fills it, and is the right thing to put there rather than merely
    // something: engineers hold `view_actions`, cards get addressed to them
    // personally (`staff_message`, `notepad_assigned`), and until now the only
    // route to answering one was the Menu drawer. The card the office is
    // waiting on is not a menu-dive.
    //
    // NOT offered to a seat that already shows Contacts — a five-column bar is
    // full, and Actions is one tap away in the Menu for them.
    if (canViewActions) return [ACTIONS_TAB];
    // Neither: keep the spacer (`null`). On the five-column bar the column has
    // to stay empty or Ask Mucka stops being centred; on the lean bar there is
    // no centring to protect and a spacer would just put the hole back.
    return canUseChat ? [null] : [];
  });

  // What the one round button at the end of the composer means right now —
  // the same order the web button's own handler and icon follow.
  const micMode: GlassBarMic = cancelWake
    ? "cancel"
    : draft.trim()
      ? "send"
      : voiceConnecting
        ? "connecting"
        : listening
          ? "stop"
          : "mic";

  const goTo = (key: TabKey) => {
    if (key === "menu") {
      // A tab that only ever opens is a trap: the drawer covers the page, so
      // the tab you just pressed is the obvious thing to press to get out of
      // it. Toggle. menuOpen is not set here either way — MobileMenu publishes
      // it, so the highlight follows the drawer rather than our intent.
      if (menuOpen) {
        window.dispatchEvent(new CustomEvent("mobile-modals-close"));
        return;
      }
      // Browsing the menu and holding a conversation want the same space, so
      // opening one puts the other away.
      closeComposer();
      window.dispatchEvent(new CustomEvent("mobile-menu-tap"));
      return;
    }
    const path = TAB_PATHS[key];
    if (!path) return;
    // Offline: App Router client nav fetches from the server and dies exactly
    // when there's no signal. Intercept and open the saved-work overlay in
    // place instead (a tap on the tab you're already on is a no-op).
    const action = resolveNavAction(online, key, activeTab);
    if (action === "noop") return;
    if (action === "open-overlay") {
      openOfflineOverlay();
      return;
    }
    // Close any open modals (chat panel, actions stack, heads-up stack) so the
    // destination page is visible — otherwise a route change leaves the modal
    // hanging on top.
    window.dispatchEvent(new CustomEvent("mobile-modals-close"));
    closeComposer();
    // The state says online — but a live radio isn't proof of reachable
    // internet. Arm the dead-nav watch: if this push doesn't move the pathname
    // and a probe then confirms we're offline, the saved-work overlay opens
    // automatically instead of leaving the user on a silently-dead tap.
    armDeadNav();
    router.push(path);
  };

  const handleAskTap = () => {
    // Unreachable for a seat without use_chat — the tab is a plain mark with no
    // handler attached — but kept so the guard travels with the function rather
    // than living only at the one call site that exists today.
    if (!canUseChat) return;
    // Ask Mucka closes the menu: the two want the same space. The drawer
    // publishes its own close, so menuOpen follows on its own.
    if (menuOpen) window.dispatchEvent(new CustomEvent("mobile-modals-close"));
    if (composerOpen) {
      closeComposer();
      return;
    }
    setComposerOpen(true);
    // 🚨 DELIBERATELY DOES NOT FOCUS THE INPUT. Focusing raises the keyboard,
    // and the keyboard takes half the screen — so tapping Ask Mucka to READ the
    // conversation, or to reach the mic, buried it behind a keyboard nobody
    // asked for and made getting rid of it the first thing you had to do.
    //
    // This is the same call `reopenFromBanner` made from the day it was
    // written, for the same stated reason; it just never travelled to the tab
    // that most people actually open the sheet with. The keyboard now comes up
    // when the input is tapped, which is the moment it is wanted — `onFocus`
    // still drives `typing`, so the tabs slide away then rather than on open.
  };

  // MIC tap. Starting voice must NOT open the full-screen panel: the docked
  // transcript is the point. `mucka-bar-voice-start` goes straight to
  // VoiceProvider; `mobile-voice-tap` (which AuthedShell turns into an open
  // panel) is deliberately not used here.
  const handleMicTap = () => {
    if (!online) {
      // Offline Mucka can't run. Forward the tap so AuthedShell surfaces the
      // "needs a connection" nudge rather than failing silently.
      window.dispatchEvent(new CustomEvent("mobile-voice-tap"));
      return;
    }
    // 🚨 Mid-connect, the tap CANCELS. This used to return — on the reasoning
    // that there was no session to end yet — which left the only control on
    // screen inert for the whole negotiation, and inert for ever on the
    // connect that never completes. `endSession` handles it properly: it bumps
    // the generation so a pending lazy mount and fetch are invalidated, clears
    // connecting, and stops the ringing. A spinner you cannot stop is the
    // worst thing to put under somebody's thumb.
    if (voiceConnecting) {
      window.dispatchEvent(new CustomEvent("mobile-voice-end"));
      return;
    }
    if (listening) {
      window.dispatchEvent(new CustomEvent("mobile-voice-end"));
    } else {
      /**
       * 🚨 SYNCHRONOUSLY, IN THE TAP, BEFORE THE DISPATCH. `startSession`
       * fetches `/api/elevenlabs/session` before the SDK builds its
       * AudioContext, which on iOS Safari is long past the end of the gesture
       * window — the socket opens, the agent speaks, and the owner hears
       * nothing. This is the bar's mic, the DEFAULT way anybody starts a call
       * on a phone, and it never had the unlock: the start happens one window
       * event away inside VoiceProvider, so the launcher guard (which looks for
       * `startSession(`) could not see this file at all. Native is unaffected —
       * the WebView routes its own audio — so this is the browser and the PWA.
       *
       * The dispatch below runs its listeners synchronously, so the unlock and
       * the start are still the same tick as the tap.
       */
      unlockIOSAudio();
      window.dispatchEvent(new CustomEvent(BAR_VOICE_START_EVENT));
    }
  };

  // A "View Tamsin Rook" button under one of Mucka's replies. Deliberately
  // the same shape as handleTabTap rather than a bare router.push: this is a
  // client-side nav on a phone, so it needs the offline interception and the
  // dead-nav watch for exactly the reasons the tabs do.
  /** Opening a job from the list closes the bar, exactly as a nav action does
   *  — the sheet is full-screen on mobile, so the destination would otherwise
   *  open behind it. */
  const handleJobListTap = (path: string) => handleNavActionTap({ label: '', path });

  const handleNavActionTap = (nav: NavAction) => {
    if (nav.directionsTo) {
      // Hand off to the device's default maps app. Leave the bar as it is —
      // the maps app comes to the foreground itself, and closing behind it
      // would lose the conversation the user is mid-way through.
      void openDirections(nav.directionsTo);
      return;
    }
    if (!online) {
      openOfflineOverlay();
      return;
    }
    // Fold the bar away so the destination is actually visible when it lands —
    // a transcript left standing covers the record the user just asked to see.
    window.dispatchEvent(new CustomEvent("mobile-modals-close"));
    closeComposer();
    armDeadNav();
    router.push(nav.path);
  };

  // One send path for the web field and the native one.
  const sendText = (raw: string) => {
    const text = raw.trim();
    if (!text) return;
    if (!online) {
      openOfflineOverlay();
      return;
    }
    setDraft("");
    setAttachOpen(false);
    requestBarSend(text);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendText(draft);
  };

  // The picker, the upload and the hidden file inputs all live in MuckaPanel,
  // so the bar asks rather than reimplementing them — but it stays put. This
  // used to open the full panel, which threw you out of the page you were
  // attaching a photo to, which is the one thing the bar exists to prevent.
  const handleAttachPick = (kind: BarAttachKind) => {
    setAttachOpen(false);
    window.dispatchEvent(new CustomEvent(BAR_ATTACH_EVENT, { detail: { kind } }));
  };

  const badgeTab = canViewActions && !canViewClients ? "actions" : "menu";
  const renderTab = (tab: Tab) => {
    const isActive = activeTab === tab.key;
    const count = glass && tab.key === badgeTab && actionsWaiting > 0 ? actionsWaiting : 0;
    return (
      <button
        key={tab.key}
        type="button"
        className={`mucka-bar-tab${isActive ? " mucka-bar-tab--current" : ""}`}
        aria-current={isActive && tab.key !== "menu" ? "page" : undefined}
        aria-expanded={tab.key === "menu" ? menuOpen : undefined}
        onClick={() => goTo(tab.key)}
      >
        <span className="mucka-bar-tab-ico">
          <Icon icon={tab.icon} size={21} strokeWidth={2.1} />
          {count > 0 && (
            <b className="mucka-bar-tab-n" aria-label={`${count} ${count === 1 ? "action" : "actions"} waiting`}>
              {count > 99 ? "99+" : count}
            </b>
          )}
        </span>
        <span>{tab.label}</span>
      </button>
    );
  };

  return (
    <div
      className={`mucka-bar-wrap${darkMode ? " mucka-bar-wrap--dark" : ""}`}
      role="navigation"
      aria-label="Primary"
    >
      {nativeBar && (
        <NativeGlassBar
          left={[...LEFT_TABS, ...subbieWorkTabs]}
          right={rightColumn}
          ask={canUseChat}
          active={activeTab}
          dark={darkMode}
          mode={composerOpen ? "composer" : typing || searchFocused ? "hidden" : "tabs"}
          placeholder={composerPlaceholder}
          mic={micMode}
          attachOpen={attachOpen}
          dimmed={voiceReconnecting}
          foldIcon={foldIcon}
          // Never: the full-screen chat has its X, and the minimised pill has
          // its own (Tom, 2026-10-10). Sent for builds that still draw one.
          fold={false}
          draft={draft}
          onTab={(key) => goTo(key as TabKey)}
          onAsk={handleAskTap}
          onFold={closeComposer}
          onAttach={() => {
            setAttachOpen((v) => !v);
            setChatMinimised(false);
          }}
          onMic={() => {
            if (cancelWake) cancelWake();
            else if (draft.trim()) sendText(draft);
            else handleMicTap();
          }}
          onSubmit={sendText}
          onDraft={setDraft}
          onFocus={() => setTyping(true)}
          onBlur={() => setTyping(false)}
        />
      )}
      <div
        ref={sheetRef}
        className={`mucka-bar-sheet${composerOpen ? " mucka-bar-sheet--open" : ""}${listening ? " mucka-bar-sheet--talking" : ""}${typing ? " mucka-bar-sheet--typing" : ""}${darkMode ? " mucka-bar-sheet--dark" : ""}${composerOpen && chatMinimised ? " mucka-bar-sheet--chatmin" : ""}${convoFull ? " mucka-bar-sheet--convo" : ""}`}
      >
        {(composerOpen || voiceLive) && (
          // The caret fills the header so the grip stays a full-width thumb
          // target; the refresh button sits on top of it at the right rather
          // than inside it, because a button cannot contain another button.
          //
          // Rendered while a call is live even with the composer away, so the
          // minimised state is a visible orange banner rather than an ordinary
          // bar with a mic running behind it.
          <div className={`mucka-bar-header${voiceLive ? " mucka-bar-header--live" : ""}`}>
            <button
              type="button"
              className={`mucka-bar-caret${showTranscript ? " mucka-bar-caret--grip" : ""}${dragging ? " mucka-bar-caret--dragging" : ""}`}
              aria-label={
                barMinimised
                  ? "Still listening — tap to bring the conversation back"
                  : showTranscript
                    ? "Drag to resize the conversation, or tap to hide it"
                    : "Hide the message box"
              }
              onPointerDown={onGripDown}
              onPointerMove={onGripMove}
              onPointerUp={onGripUp}
              onPointerCancel={onGripUp}
              onClick={() => {
                // Minimised: the banner is the only control there is, and it
                // has one job — put the conversation back. The pointer
                // handlers above all return early without a transcript, so
                // this is the only thing a tap can reach.
                if (barMinimised) {
                  reopenFromBanner();
                  return;
                }
                // With a transcript up, taps route through the pointer handlers
                // so a drag never counts as a dismiss.
                if (!showTranscript) closeComposer();
              }}
            >
              <Icon icon={barMinimised ? ChevronUp : ChevronDown} size={18} strokeWidth={2.5} />
            </button>
            {/* Liquid Glass (variant B) draws the minimised call as a slim bar
                with the M, the last line and a live waveform. Rendered for
                everyone, drawn only under :root[data-ui="glass"] — variant A
                keeps its plain orange banner. The caret above carries the
                name and the tap. */}
            {barMinimised && (
              <span className="mucka-bar-minline" aria-hidden="true">
                <span className="mucka-bar-askmark" />
                <span className="mucka-bar-minline-text">{lastLine ?? "Mucka is listening"}</span>
                <span className="mucka-bar-wave"><i /><i /><i /><i /></span>
              </span>
            )}
            {barMinimised && (
              <button
                type="button"
                className="mucka-bar-end"
                aria-label="End the call"
                onClick={endConversation}
              >
                <Icon icon={X} size={18} strokeWidth={2.25} />
              </button>
            )}
            {/* Liquid Glass (variant B): the full-screen conversation's header
                — who, how she is, and the minimise button. Drawn only under
                :root[data-ui="glass"]; variant A keeps its caret. */}
            {composerOpen && (
              <>
                <span className="mucka-bar-who">
                  <span className="mucka-bar-askmark" aria-hidden="true" />
                  <span className="mucka-bar-who-text">
                    <strong>Mucka</strong>
                    <span className="mucka-bar-status">
                      {listening && <span className="mucka-bar-wave" aria-hidden="true"><i /><i /><i /><i /></span>}
                      {chatStatus}
                    </span>
                  </span>
                </span>
                <button
                  type="button"
                  className="mucka-bar-minimise"
                  aria-label="Minimise and keep using the app"
                  onClick={() => {
                    setChatMinimised(true);
                    setAttachOpen(false);
                  }}
                >
                  <Icon icon={ChevronDown} size={20} strokeWidth={2.25} />
                </button>
                <button
                  type="button"
                  className="mucka-bar-end"
                  aria-label={listening ? "End the call and close" : "Close the conversation"}
                  onClick={endConversation}
                >
                  <Icon icon={X} size={20} strokeWidth={2.25} />
                </button>
              </>
            )}
            {/* Start-again is hidden while minimised — `showTranscript`
                requires the composer, so this is already false there. Wiping a
                conversation you cannot see would be the wrong thing to offer
                anyway. */}
            {showTranscript && (
              <button
                type="button"
                className="mucka-bar-reset"
                aria-label="Start a new conversation"
                onClick={() => window.dispatchEvent(new CustomEvent(BAR_RESET_EVENT))}
              >
                <Icon icon={RefreshCw} size={16} strokeWidth={2.25} />
              </button>
            )}
          </div>
        )}

        {showTranscript && (
          // The wrapper exists only to anchor the reconnecting pill over the
          // transcript (TKT-1462): on a phone THIS is the conversation, not
          // MuckaPanel's, so the blur shipped there was never seen.
          <div className="mucka-bar-transcript-wrap">
          {voiceReconnecting && (
            <div className="mucka-bar-reconnecting" role="status" aria-live="polite">
              <span className="mp-reconnecting-pill">
                Reconnecting<span className="mp-reconnecting-dots" aria-hidden="true"><i /><i /><i /></span>
              </span>
            </div>
          )}
          <div
            className={`mucka-bar-transcript${voiceReconnecting ? " mucka-bar-transcript--reconnecting" : ""}`}
            ref={scrollRef}
            style={{ maxHeight: transcriptH }}
            aria-hidden={voiceReconnecting || undefined}
          >
            {chat.messages.map((msg, i) => (
              <Fragment key={i}>
              <div className={`mucka-bar-msg mucka-bar-msg--${msg.role}`}>
                {msg.attachment && (
                  <div className="mucka-bar-bubble mucka-bar-bubble--attachment">
                    {msg.attachment.kind === "image" && msg.attachment.url ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={msg.attachment.url} alt={msg.attachment.filename ?? "Photo"} />
                    ) : (
                      <span className="mucka-bar-attachchip">
                        <Icon icon={ATTACH_ICONS[msg.attachment.kind]} size={16} strokeWidth={2} />
                        {msg.attachment.filename ??
                          (msg.attachment.kind === "location" ? "Location"
                            : msg.attachment.kind === "video" ? "Video"
                            : "Photo")}
                      </span>
                    )}
                    {(msg.attachment.uploading || msg.attachment.failed) && (
                      <span className="mucka-bar-attachstate">
                        {msg.attachment.failed ? "Upload failed" : "Sending…"}
                      </span>
                    )}
                  </div>
                )}
                {(msg.text || msg.stagedActions?.length || msg.jobList?.length || msg.navActions?.length || msg.choices || msg.deleteAction || msg.blocks?.length) && (
                  <div className="mucka-bar-bubble">
                    {msg.text && <div className="mchat-body">{msg.text}</div>}
                    {/* The same components as the panel, from the same file.
                        A step is ASKED of MuckaPanel, which saves it. */}
                    {msg.role === "mucka" && (
                      <UiBlocks
                        blocks={msg.blocks}
                        editorStates={chat.editorStates}
                        canEditQuotes={chat.canEditQuotes === true}
                        live={!chat.thinking}
                        onLineStep={requestBarLineStep}
                        onAnswer={requestBarSend}
                        onOpen={handleJobListTap}
                      />
                    )}
                    {/* 🚨 The approval is a strip INSIDE her bubble, not a card
                        beside it. It draws here at all because of TKT-1325 —
                        the bar is the default chat on a phone and the card had
                        never once rendered on it. The bar still only ASKS;
                        MuckaPanel owns the decision. */}
                    {/* Rows — a day's work, a client to open, directions.
                        They were full-width orange buttons; a destination is a
                        row now, because orange means it sends or changes
                        something. Same helper as the panel, so the two cannot
                        describe the same job differently. */}
                    {msg.choices && (
                      <ChoiceChips
                        choices={msg.choices}
                        answered={msg.choiceAnswer ?? null}
                        live={i === lastChoiceIndex && !chat.thinking}
                        onPick={(label) => requestBarChoicePick(i, label)}
                      />
                    )}
                    <BubbleRows
                      label={msg.jobList?.length ? `${msg.jobList.length} jobs` : undefined}
                      rows={[
                        ...(msg.jobList?.length && !blocksReplaceJobRows(msg.blocks) ? jobListRows(msg.jobList, handleJobListTap) : []),
                        ...(msg.navActions?.length
                          ? navActionRows(msg.navActions, {
                              onOpen: (path) => handleNavActionTap({ label: '', path }),
                              onDirections: (destination) => handleNavActionTap({ label: '', path: '', directionsTo: destination }),
                            })
                          : []),
                      ]}
                    />
                    {msg.stagedActions?.map((sa) => {
                      const st = chat.stagedStates[sa.id] || 'idle';
                      // A document card in this reply already shows this card with its Send.
                      if (stagedAt.get(sa.id) !== i || st === 'dismissed' || documentCardActionIds(msg.blocks).has(sa.id)) return null;
                      return (
                        <Fragment key={sa.id}>
                          {/* 🚨 The queued thing states itself, ALWAYS — even though her
                              sentence usually says it too. This block is often RECOVERED from
                              its own engine_actions row rather than delivered live (see
                              recover-staged.ts), and the reply it hangs off is then whatever
                              she happened to be saying — frequently "there's a card just
                              below", which names no amount and no address. Nothing is
                              approved here that has not written out what it is. */}
                          <p className="mchat-staged" data-staged-id={sa.id}>{sa.body}</p>
                          <BubbleActions
                            done={st === 'approved' ? sa.primaryDoneLabel : null}
                            actions={[
                              {
                                label: st === 'error' ? 'Try again' : sa.primaryLabel,
                                busyLabel: 'Sending…',
                                busy: st === 'approving',
                                onPress: () => requestBarStagedDecision(sa.id, 'approved'),
                              },
                              {
                                label: st === 'error' ? 'Leave it' : 'Not yet',
                                tone: 'quiet',
                                disabled: st === 'approving',
                                onPress: () => requestBarStagedDecision(sa.id, 'dismissed'),
                              },
                            ]}
                          />
                          {st === 'error' && (
                            <p className="mchat-body mchat-error">{chat.stagedErrors?.[sa.id] || 'That didn’t go through. Nothing was sent.'}</p>
                          )}
                        </Fragment>
                      );
                    })}
                    {/* 🚨 TKT-1345 — the delete confirmation, which drew only in
                        MuckaPanel while the engine told the owner it was on
                        screen. Same shape as the panel's; MuckaPanel runs it. */}
                    {msg.deleteAction && chat.deleteStates[i] !== 'dismissed' && (
                      <>
                        <p className="mchat-staged">{msg.deleteAction.summary}</p>
                        <BubbleActions
                          done={chat.deleteStates[i] === 'deleted' ? 'Deleted' : null}
                          actions={[
                            {
                              label: 'Delete it',
                              tone: 'destructive',
                              busyLabel: 'Deleting…',
                              busy: chat.deleteStates[i] === 'deleting',
                              onPress: () => requestBarDelete(i, 'delete'),
                            },
                            {
                              label: 'Keep it',
                              tone: 'quiet',
                              disabled: chat.deleteStates[i] === 'deleting',
                              onPress: () => requestBarDelete(i, 'keep'),
                            },
                          ]}
                        />
                      </>
                    )}
                  </div>
                )}
                {msg.time && (
                  <div className="mucka-bar-meta">
                    <span>{formatBubbleTime(msg.time)}</span>
                    {msg.role === "user" && (
                      <svg
                        width="14"
                        height="10"
                        viewBox="0 0 16 10"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="1 5 4 8 9 2" />
                        <polyline points="6 5 9 8 14 2" />
                      </svg>
                    )}
                  </div>
                )}
              </div>
              </Fragment>
            ))}
            {chat.thinking && (
              <div className="mucka-bar-msg mucka-bar-msg--mucka">
                {chat.thinkingStatus ? (
                  <div className="mucka-bar-bubble mucka-bar-bubble--status" role="status">{chat.thinkingStatus}</div>
                ) : (
                  <div className="mucka-bar-bubble mucka-bar-bubble--thinking" aria-label="Mucka is typing">
                    <span /><span /><span />
                  </div>
                )}
              </div>
            )}
          </div>
          </div>
        )}

        {composerOpen && attachOpen && (
          <div className="mucka-bar-attachgrid">
            {ATTACH_ITEMS.map((item) => (
              <button
                key={item.key}
                type="button"
                className="mucka-bar-attachbtn"
                onClick={() => handleAttachPick(item.key)}
              >
                <Icon icon={item.icon} size={18} strokeWidth={2} />
                {item.label}
              </button>
            ))}
          </div>
        )}

        {/* Liquid Glass (variant B): the minimised conversation — a slim bar
            above the composer with the last line. Only reachable from the
            glass minimise button, so variant A never renders it. */}
        {composerOpen && chatMinimised && (
          <div className="mucka-bar-chatmini">
            <button
              type="button"
              className="mucka-bar-chatmini-open"
              aria-label="Open the conversation"
              onClick={() => setChatMinimised(false)}
            >
              <span className="mucka-bar-askmark" aria-hidden="true" />
              <span className="mucka-bar-minline-text">{lastLine ?? "Ready when you are"}</span>
              {listening && <span className="mucka-bar-wave" aria-hidden="true"><i /><i /><i /><i /></span>}
            </button>
            <button
              type="button"
              className="mucka-bar-end"
              aria-label={listening ? "End the call and close" : "Close the conversation"}
              onClick={endConversation}
            >
              <Icon icon={X} size={18} strokeWidth={2.25} />
            </button>
          </div>
        )}

        {composerOpen && (
          <form
            className={`mucka-bar-composer${cancelWake ? " mucka-bar-composer--cancelwake" : ""}${voiceReconnecting ? " mucka-bar-composer--reconnecting" : ""}`}
            onSubmit={handleSubmit}
          >
            {/* 🚨 An overlay, not an onClick on the row. The row contains a
                text input and a mic button, each of which takes a tap of its
                own, so "the whole row is the button" is only true if something
                is genuinely laid over the whole row. The words the user reads
                are the input's placeholder underneath; this carries the
                accessible name and the tap. */}
            {cancelWake && (
              <button
                type="button"
                className="mucka-bar-cancelwake"
                aria-label="Cancel — Mucka heard the wake word"
                onClick={cancelWake}
              />
            )}
            <button
              type="button"
              className={`mucka-bar-attach${attachOpen ? " mucka-bar-attach--open" : ""}`}
              aria-label={attachOpen ? "Close the attach menu" : "Add a photo or file"}
              aria-expanded={attachOpen}
              // Tapping a <button> moves focus off the input, which drops the
              // keyboard — so the grid opened while the whole sheet fell down
              // the screen after it. Refusing the focus keeps the caret (and
              // the keyboard) exactly where it was, so only the grid moves.
              // Deliberately not on the mic: that one IS a switch to voice, and
              // the keyboard going away is the right answer there.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                const hadFocus = document.activeElement === inputRef.current;
                setAttachOpen((v) => !v);
                // Variant B: attaching brings a minimised conversation back.
                setChatMinimised(false);
                // 🚨 Only put the caret back if it was ALREADY there. This
                // call is belt and braces for WebViews that focus on touchend
                // rather than mousedown — but unconditionally it also RAISES a
                // keyboard that was down, so tapping the plus to reach the four
                // attach buttons threw the keyboard up over them. Restore
                // focus, never summon it.
                if (hadFocus) inputRef.current?.focus();
              }}
            >
              <Icon icon={Plus} size={22} strokeWidth={2.25} />
            </button>
            <input
              ref={inputRef}
              className="mucka-bar-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              // Focus, not keyboard geometry, is what hides the tabs — see the
              // `typing` note where it is used.
              onFocus={() => setTyping(true)}
              onBlur={() => setTyping(false)}
              // Connecting takes precedence: the spinning mic sits under the
              // thumb that just tapped it, so the placeholder is what actually
              // gets seen.
              // 🚨 "Listening…" here means a LIVE CALL is listening to you.
              // A dropped line says nothing here: the pill over the blurred
              // conversation already says "Reconnecting", and the field is
              // blurred with it (Tom, 8 Oct 2026). The armed wake word says
              // nothing either, by Tom's call the same day: the field reads
              // "Ask Mucka…" and the phone's own microphone dot is the sign.
              // 🚨 The cancel outranks the connecting label, and that ordering
              // is the feature. "Connecting…" is what the mic button says and
              // it is fine there, because you pressed it. For a session you did
              // not ask for, the row has to say what STARTED it and offer the
              // way out — for the couple of seconds where there is still
              // something to get out of.
              placeholder={composerPlaceholder}
              aria-label="Ask Mucka"
              enterKeyHint="send"
            />
            <button
              type={draft.trim() && !cancelWake ? "submit" : "button"}
              className={`mucka-bar-mic${voiceConnecting ? " mucka-bar-mic--connecting" : ""}`}
              aria-label={
                draft.trim()
                  ? "Send"
                  : voiceConnecting
                    ? "Connecting to Mucka"
                    : listening
                      ? "Stop talking to Mucka"
                      : "Talk to Mucka"
              }
              aria-pressed={listening}
              aria-busy={voiceConnecting}
              onClick={cancelWake ?? (draft.trim() ? undefined : handleMicTap)}
            >
              {cancelWake ? <Icon icon={X} size={20} strokeWidth={2.25} /> : draft.trim() ? (
                <Icon icon={Send} size={19} strokeWidth={2} />
              ) : voiceConnecting ? (
                <Icon icon={LoaderCircle} size={20} strokeWidth={2} className="mucka-bar-spinner" />
              ) : (
                // A stop square, not a crossed-out mic: a slashed mic reads as
                // MUTED, so a live call looked switched off and tapping it to
                // end the call looked like switching it on (TKT-1457).
                listening ? (
                  <Icon icon={Square} size={16} strokeWidth={2} fill="currentColor" />
                ) : (
                  <Icon icon={Mic} size={20} strokeWidth={2} />
                )
              )}
            </button>
          </form>
        )}

        {/* Only once there is something to caveat. On the bare composer it
            would be a warning about output that does not exist yet. */}
        {composerOpen && (showTranscript || listening || voiceConnecting) && (
          <p className="mucka-bar-disclaimer">
            AI may make mistakes. Verify important information.
          </p>
        )}

        {/* Hidden while typing rather than removed: the bar is a 5-column grid
            and unmounting the row would collapse the sheet's height mid-tap. */}
        {/* `--lean` drops the grid from five columns to however many tabs are
            actually rendered. It exists for the subcontractor seat, which has
            neither Ask Mucka nor Clients: five columns with two of them empty
            left Home and Diary huddled against the left edge with a void where
            the brand mark used to be. The empty-slot trick below is still right
            for everyone ELSE, where only Clients is missing and Ask Mucka has
            to stay dead-centre. */}
        <nav
          className={`mucka-bar-tabs${canUseChat ? "" : " mucka-bar-tabs--lean"}${typing || searchFocused ? " mucka-bar-tabs--away" : ""}`}
        >
          {LEFT_TABS.map(renderTab)}

          {subbieWorkTabs.map(renderTab)}

          {canUseChat && (
            <button
              type="button"
              className={`mucka-bar-tab mucka-bar-tab--ask${composerOpen ? " mucka-bar-tab--current" : ""}`}
              aria-expanded={composerOpen}
              onClick={handleAskTap}
            >
              <span className="mucka-bar-tab-ico">
                <span className="mucka-bar-askmark" aria-hidden="true" />
              </span>
              <span>Ask Mucka</span>
            </button>
          )}

          {rightColumn.map((tab, i) => {
            if (tab) return renderTab(tab);
            // The engineer's column with neither Contacts nor Actions: on the
            // five-column bar it has to stay empty or Ask Mucka stops being
            // centred. (Why Actions fills it when it can: see rightColumn.)
            return <div key={`slot-${i}`} className="mucka-bar-slot" aria-hidden="true" />;
          })}
        </nav>
      </div>
    </div>
  );
}
